import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="container m-auto p-5">
      <div className="card  p-4">
        <h1 className="display-4 mb-3 text-start">TeamSync Privacy Policy</h1>
        <p className="text-muted mb-4 text-start">Effective date: May 2025</p>

        <section className="mb-5">
          <p className="text-start">
            This Privacy Policy for Scott Creative LLP ("we," "us," or "our"),
            describes how and why we might access, collect, store, use, and/or
            share ("process") your personal information when you use our
            services, including when you:
          </p>
          <ul className="list-group list-group-flush mt-1">
            <li className="list-group-item text-start">
              Download and use our mobile application (TeamSync)
            </li>
            <li className="list-group-item text-start">
              Use TeamSync: Synchronized for Success. Say hello to simplified HR
              management with TeamSync, the all-in-one platform designed to
              connect your people and processes seamlessly. Effortlessly manage
              your team's attendance with customizable policies and real-time
              tracking. Empower employees with a user-friendly self-service
              portal for leave requests and gain clear approval workflows. But
              TeamSync is more than just the essentials. We also help you
              maintain a centralized and organized employee database, track and
              manage team performance. Gain valuable insights into your
              workforce with comprehensive reporting and analytics, enabling you
              to make data-driven decisions. TeamSync is built for intuitive
              use, freeing up valuable time so you can focus on what truly
              matters - your people. Embrace a new era of efficient and
              effective HR management. Get TeamSync today and experience the
              power of being truly synchronized for success!
            </li>
            <li className="list-group-item text-start">
              Engage with us in other related ways, including any sales,
              marketing, or events
            </li>
          </ul>
          <p className="mt-3 text-start">
            Questions or concerns? Reading this Privacy Policy will help you
            understand your privacy rights and choices. We are responsible for
            making decisions about how your personal information is processed.
            If you do not agree with our policies and practices, please do not
            use our Services. If you still have any questions or concerns,
            please contact us at{" "}
            <a href="mailto:hr@jjretail.in" className="text-primary">
              hr@jjretail.in
            </a>
            .
          </p>
        </section>

        <section className="mb-5">
          <h2 className="h3 mb-3 text-start">SUMMARY OF KEY POINTS</h2>
          <p className="text-start">
            This summary provides key points from our Privacy Policy, by using
            our table of contents below to find the section you are looking for.
          </p>
          <ul className="list-group list-group-flush mt-1">
            <li className="list-group-item text-start">
              <strong>What personal information do we process?</strong> We may
              process personal information depending on how you interact with us
              and the Services, and the products and features you use. Learn
              more about personal information you disclose to us.
            </li>
            <li className="list-group-item text-start">
              <strong>Do we process any sensitive personal information?</strong>{" "}
              Some of the information may be considered "special" or "sensitive"
              in certain jurisdictions, for example your racial or ethnic
              origins, sexual orientation, and religious beliefs. We may process
              sensitive personal information when necessary with your consent or
              as otherwise permitted by applicable law.
            </li>
            <li className="list-group-item text-start">
              <strong>Do we collect any information from third parties?</strong>{" "}
              We do not collect any information from third parties.
            </li>
            <li className="list-group-item text-start">
              <strong>How do we process your information?</strong> We process
              your information to provide, improve, and administer our Services,
              communicate with you, for security and fraud prevention, and to
              comply with law. We may also process your information for other
              purposes with your consent. We process your information only when
              we have a valid legal reason to do so.
            </li>
            <li className="list-group-item text-start">
              <strong>
                In what situations and with which parties do we share personal
                information?
              </strong>{" "}
              We may share information in specific situations and with specific
              third parties.
            </li>
            <li className="list-group-item text-start">
              <strong>What are your rights?</strong> Depending on where you are
              located geographically, the applicable privacy law may mean you
              have certain rights regarding your personal information.
            </li>
            <li className="list-group-item text-start">
              <strong>How do you exercise your rights?</strong> The easiest way
              to exercise your rights is by submitting a data subject access
              request, or by contacting us. We will consider and act upon any
              request in accordance with applicable data protection laws.
            </li>
            <li className="list-group-item text-start">
              <strong>
                Want to learn more about what we do with any information we
                collect?
              </strong>{" "}
              Review the Privacy Policy in full.
            </li>
          </ul>
        </section>

        <section className="mb-5">
          <h2 className="h3 mb-3 text-start">TABLE OF CONTENTS</h2>
          <ol className="list-group list-group-numbered">
            <li className="list-group-item text-start">
              <a href="#section1" className="text-primary">
                What information do we collect?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section2" className="text-primary">
                How do we process your information?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section3" className="text-primary">
                When and with whom do we share your personal information?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section4" className="text-primary">
                What is our stance on third-party websites?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section5" className="text-primary">
                How long do we keep your information?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section6" className="text-primary">
                Do we collect information from minors?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section7" className="text-primary">
                What are your privacy rights?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section8" className="text-primary">
                Controls for DO-NOT-TRACK features
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section9" className="text-primary">
                Do other regions have specific privacy rights?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section10" className="text-primary">
                Do we make updates to this policy?
              </a>
            </li>
            <li className="list-group-item text-start">
              <a href="#section11" className="text-primary">
                How can you contact us about this policy?
              </a>
            </li>
          </ol>
        </section>

        <section id="section1" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            1. What information do we collect?
          </h2>
          <h3 className="h4 mb-2 text-start">
            Personal information you disclose to us
          </h3>
          <p className="text-start">
            We collect personal information that you provide to us.
          </p>
          <p className="text-start">
            We collect personal information that you voluntarily provide to us
            when you register on the Services, express an interest in obtaining
            information about us or our products and Services, when you
            participate in activities on the Services, or otherwise when you
            contact us.
          </p>
          <p className="text-start">
            <strong>Personal Information Provided by You.</strong> The personal
            information that we collect depends on the context of your
            interactions with us and the Services, the choices you make, and the
            products and features you use. The personal information we collect
            may include the following:
          </p>
          <ul className="list-group list-group-flush mt-1">
            <li className="list-group-item text-start">Names</li>
            <li className="list-group-item text-start">Phone numbers</li>
            <li className="list-group-item text-start">Email addresses</li>
            <li className="list-group-item text-start">Job titles</li>
            <li className="list-group-item text-start">Passwords</li>
            <li className="list-group-item text-start">Contact preferences</li>
            <li className="list-group-item text-start">
              Contact or authentication data
            </li>
          </ul>
          <p className="mt-3 text-start">
            <strong>Sensitive Information.</strong> When necessary, with your
            consent or as otherwise permitted by applicable law, we process the
            following categories of sensitive information:
          </p>
          <ul className="list-group list-group-flush mt-1">
            <li className="list-group-item text-start">Student data</li>
          </ul>
          <p className="mt-3 text-start">
            All personal information that you provide to us must be true,
            complete, and accurate, and you must notify us of any changes to
            such personal information.
          </p>
          <p className="mt-3 text-start">
            <strong>In Short:</strong> Some information — such as your Internet
            Protocol (IP) address and/or browser and device characteristics — is
            collected automatically when you visit our Services. The information
            does not reveal your specific identity (like your name or contact
            information).
          </p>
          <p className="mt-3 text-start">
            The information we collect includes:
          </p>
          <ul className="list-group list-group-flush mt-1">
            <li className="list-group-item text-start">
              Log and usage data is service-related, diagnostic, usage, and
              performance information our servers automatically collect when you
              access or use our Services and which we record in log files.
              Depending on how you interact with us, this log data may include
              your activity in the Services (such as the date/time stamps),
              device event information (such as system activity, error reports
              (sometimes called "crash dumps"), and hardware settings).
            </li>
          </ul>
          <p className="mt-3 text-start">
            <strong>Google API:</strong> Our use of information received from
            Google APIs will adhere to Google API Services User Data Policy,
            including the Limited Use requirements.
          </p>
        </section>

        <section id="section2" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            2. How do we process your information?
          </h2>
          <p className="text-start">
            We process your information to provide, improve, and administer our
            Services, communicate with you, for security and fraud prevention,
            and to comply with law. We may also process your information for
            other purposes with your consent.
          </p>
          <p className="mt-3 text-start">
            We process your personal information for a variety of reasons,
            depending on how you interact with our Services, including:
          </p>
          <ul className="list-group list-group-flush mt-1">
            <li className="list-group-item text-start">
              To facilitate account creation and authentication and otherwise
              manage user accounts. We may process your information so you can
              create and log in to your account, as well as keep your account in
              working order.
            </li>
            <li className="list-group-item text-start">
              To enable user-to-user communications. We may process your
              information if you choose to use any of our offerings that allow
              for communication with another user.
            </li>
          </ul>
        </section>

        <section id="section3" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            3. When and with whom do we share your personal information?
          </h2>
          <p className="text-start">
            We may share information in specific situations described in this
            section and/or with the following third parties.
          </p>
          <p className="mt-3 text-start">
            We may need to share your personal information in the following
            situations.
          </p>
        </section>

        <section id="section4" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            4. What is our stance on third-party websites?
          </h2>
          <p className="text-start">
            We are not responsible for the safety of any information that you
            share with third parties that we may link to or who advertise on our
            Services, but are not affiliated with, our Services.
          </p>
          <p className="mt-3 text-start">
            The Services may link to third-party websites, online services, or
            mobile applications. We do not make any guarantee regarding any
            third parties, and we will not be liable for any loss or damage
            caused by the use of third-party websites, services, or mobile
            applications. The inclusion of a link towards a third-party website,
            service, or mobile application does not imply an endorsement by us.
            Any data collected by third parties is not covered by this Privacy
            Policy. We are not responsible for the content or privacy and
            security practices and policies of any third parties, including
            other websites, services, or mobile applications that may be linked
            to or from the Services. You should review the policies of such
            third parties and contact them directly to respond to your
            questions.
          </p>
        </section>

        <section id="section5" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            5. How long do we keep your information?
          </h2>
          <p className="text-start">
            We keep your information for as long as necessary to fulfill the
            purposes outlined in this Privacy Policy unless otherwise required
            by law.
          </p>
          <p className="mt-3 text-start">
            We will only keep your personal information for as long as it is
            necessary for the purposes set out in this Privacy Policy, unless a
            longer retention period is required or permitted by law (such as
            tax, accounting, or other legal requirements). No purpose in this
            Policy will require us to keep your personal information for longer
            than the period of time in which users have an account with us.
          </p>
          <p className="mt-3 text-start">
            When we have no ongoing legitimate business need to process your
            personal information, we will either delete or anonymize such
            information, or, if this is not possible (for example, because your
            personal information has been stored in backup archives), then we
            will securely store your personal information and isolate it from
            any further processing until deletion is possible.
          </p>
        </section>

        <section id="section6" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            6. Do we collect information from minors?
          </h2>
          <p className="text-start">
            We do not knowingly collect data from or market to children under 18
            years of age.
          </p>
          <p className="mt-3 text-start">
            We do not knowingly collect, solicit data from, or market to
            children under 18 years of age, nor do we knowingly sell such
            personal information. By using the Services, you represent that you
            are at least 18 or that you are the parent or guardian of such a
            minor and consent to such minor dependent's use of the Services. If
            we learn that personal information from users less than 18 years of
            age has been collected, we will deactivate the account and take
            reasonable measures to promptly delete such data from our records.
            If you become aware of any data we may have collected from children
            under age 18, please contact us at{" "}
            <a href="mailto:hr@jjretail.in" className="text-primary">
              hr@jjretail.in
            </a>
            .
          </p>
        </section>

        <section id="section7" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            7. What are your privacy rights?
          </h2>
          <p className="text-start">
            You may review, change, or terminate your account at any time,
            depending on your country, province, or state of residence.
          </p>
          <p className="mt-3 text-start">
            <strong>Withdrawing your consent:</strong> If we are relying on your
            consent to process your personal information, which may be express
            and/or implied consent depending on the applicable law, you have the
            right to withdraw your consent at any time. You can withdraw your
            consent at any time by contacting us by using the contact details
            provided in the section "HOW CAN YOU CONTACT US ABOUT THIS POLICY?"
            below.
          </p>
          <p className="mt-3 text-start">
            However, please note that this will not affect the lawfulness of the
            processing before its withdrawal nor, when applicable law allows,
            will it affect the processing of your personal information conducted
            in reliance on lawful processing grounds other than consent.
          </p>
          <p className="mt-3 text-start">
            <strong>Account Information</strong>
          </p>
          <p className="text-start">
            If you would at any time like to review or change the information in
            your account or terminate your account, you can:
          </p>
          <ul className="list-group list-group-flush mt-1">
            <li className="list-group-item text-start">
              Contact us using the contact information provided.
            </li>
          </ul>
          <p className="mt-3 text-start">
            Upon your request to terminate your account, we will deactivate or
            delete your account and information from our active databases.
            However, we may retain some information in our files to prevent
            fraud, troubleshoot problems, assist with any investigations,
            enforce our legal terms and/or comply with applicable legal
            requirements.
          </p>
          <p className="mt-3 text-start">
            If you have questions or comments about your privacy rights, you may
            email us at{" "}
            <a href="mailto:hr@jjretail.in" className="text-primary">
              hr@jjretail.in
            </a>
            .
          </p>
        </section>

        <section id="section8" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            8. Controls for DO-NOT-TRACK features
          </h2>
          <p className="text-start">
            Most web browsers and some mobile operating systems and mobile
            applications include a Do-Not-Track ("DNT") feature or setting you
            can activate to signal your privacy preference not to have data
            about your online browsing activities monitored and collected. At
            this stage, no uniform technology standard for recognizing and
            implementing DNT signals has been finalized. As such, we do not
            currently respond to DNT browser signals or any other mechanism that
            automatically communicates your choice not to be tracked online. If
            a standard for online tracking is adopted that we must follow in the
            future, we will inform you about that practice in a revised version
            of this Privacy Policy.
          </p>
        </section>

        <section id="section9" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            9. Do other regions have specific privacy rights?
          </h2>
          <p className="text-start">
            You may have additional rights based on the country you reside in.
          </p>
          <p className="mt-3 text-start">
            <strong>Australia.</strong> We collect and process your personal
            information under the obligations and conditions set by Australia's
            Privacy Act 1988 (Privacy Act).
          </p>
          <p className="text-start">
            This Privacy Policy satisfies the Policy requirements defined in the
            Privacy Act, in particular: what personal information we collect
            from you, from which sources, for which purposes, and other
            recipients of your personal information.
          </p>
          <p className="text-start">
            If you do not wish to provide the personal information necessary to
            fulfill their applicable purpose, it may affect our ability to
            provide our services, in particular:
          </p>
          <ul className="list-group list-group-flush mt-1">
            <li className="list-group-item text-start">
              Offer you the products or services that you want
            </li>
            <li className="list-group-item text-start">
              Respond to or help with your requests
            </li>
            <li className="list-group-item text-start">
              Manage your account with us
            </li>
            <li className="list-group-item text-start">
              Confirm your identity and protect your account
            </li>
          </ul>
          <p className="mt-3 text-start">
            At any time, you have the right to request access to or correction
            of your personal information. You can make such a request by
            contacting us by using the contact details provided in the section
            "HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM
            YOU?"
          </p>
          <p className="text-start">
            If you believe we are unlawfully processing your personal
            information, you have the right to submit a complaint about a breach
            of the Australian Privacy Principles to the Office of the Australian
            Information Commissioner.
          </p>
        </section>

        <section id="section10" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            10. Do we make updates to this policy?
          </h2>
          <p className="text-start">
            Yes, we will update this Policy as necessary to stay compliant with
            relevant laws.
          </p>
          <p className="mt-3 text-start">
            We may update this Privacy Policy from time to time. The updated
            version will be indicated by an updated "Revised" date at the top of
            this Privacy Policy. If we make material changes to this Privacy
            Policy, we may notify you either by prominently posting a Policy of
            such changes or by directly sending you a notification. We encourage
            you to review this Privacy Policy frequently to be informed of how
            we are protecting your information.
          </p>
        </section>

        <section id="section11" className="mb-5">
          <h2 className="h3 mb-3 text-start">
            11. How can you contact us about this policy?
          </h2>
          <p className="text-start mb-5">
            If you have questions or comments about this Policy, you may email
            us at{" "}
            <a href="mailto:hr@jjretail.in" className="text-primary">
              hr@jjretail.in
            </a>{" "}
            or contact us by post at:
          </p>
          <p className="mt-5">
            Scott Creative LLP
            <br />
            Jansons Building, Commercial Street
            <br />
            Bangalore, Karnataka 560001
            <br />
            India
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
